import { useState } from "react";
import { useMutation } from "@apollo/client";
import { Modal } from "@mantine/core";
import { noop } from "lodash";
import {
  DELETE_APPEAL_SUPPORTING_DOCUMENT,
  GET_SUPPORTING_DOCUMENTS_FOR_APPEAL,
} from "../../graphql/requests";
import { DeleteConfirmationModalView } from "../modal-views/DeleteConfirmationModalView";
import { documentTypeDisplayValueMap } from "../../types/document";
import {
  AppealSupportingDocument,
  AppealSupportingDocumentType,
} from "@/__generated__/graphql.ts";

// Type for supporting documents without isIncludedInAppeal (provider users can see all documents)
type ProviderAppealSupportingDocument = Omit<
  AppealSupportingDocument,
  "isIncludedInAppeal"
>;

type DeleteDocumentModalProps = {
  document: ProviderAppealSupportingDocument;
  isOpen: boolean;
  onClose: () => void;
  onDeleteConfirmation: () => void;
  onDeleteError: () => void;
  appealId: string;
};

export const DeleteDocumentModal = ({
  document,
  isOpen,
  onClose,
  onDeleteConfirmation,
  onDeleteError,
  appealId,
}: DeleteDocumentModalProps) => {
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [deleteAppealSupportingDocument] = useMutation(
    DELETE_APPEAL_SUPPORTING_DOCUMENT,
    {
      variables: {
        documentId: document.documentId,
      },
      refetchQueries: [
        {
          query: GET_SUPPORTING_DOCUMENTS_FOR_APPEAL,
          variables: {
            appealId,
          },
        },
      ],
    },
  );

  const handleDelete = async () => {
    setIsDeleteLoading(true);

    try {
      await deleteAppealSupportingDocument();

      onDeleteConfirmation();
    } catch (error) {
      /* oxlint-disable-next-line no-console */
      console.error("Error deleting document:", error);
      onDeleteError();
    } finally {
      setIsDeleteLoading(false);
      onClose();
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={isDeleteLoading ? noop : onClose}
      closeOnClickOutside={!isDeleteLoading}
      closeOnEscape={!isDeleteLoading}
    >
      <DeleteConfirmationModalView
        name={
          document.documentType === AppealSupportingDocumentType.Other &&
          document.otherDocumentTypeName
            ? document.otherDocumentTypeName
            : documentTypeDisplayValueMap[document.documentType]
        }
        onCancel={onClose}
        onDelete={handleDelete}
        isDeleteLoading={isDeleteLoading}
      />
    </Modal>
  );
};
